import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock, Monitor, ArrowLeft, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { INTERNSHIPS } from "@/data/internships";

export default function InternshipDetail() {
  const { slug } = useParams();
  const it = INTERNSHIPS.find((x) => x.slug === slug);
  if (!it) return <Navigate to="/internships" replace />;

  return (
    <div className="pt-24 md:pt-32">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <Link to="/internships" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-8">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All Programs
        </Link>
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full">
                {it.tag} · Internship
              </span>
              <h1 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                {it.title}
              </h1>
              <p className="mt-8 text-lg text-neutral-600 leading-relaxed">{it.short}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={150}>
              <div className="rounded-3xl overflow-hidden border border-neutral-200 aspect-[4/3]">
                <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          <InfoCard icon={MapPin} label="Location" value={it.location} />
          <InfoCard icon={Clock} label="Duration" value={it.duration} />
          <InfoCard icon={Monitor} label="Work Mode" value={it.mode} />
        </div>
      </section>

      <section className="bg-[#FAFAFA] border-y border-neutral-100 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">
              <span className="inline-block w-6 h-px bg-neutral-400" />
              Program Overview
            </span>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight leading-tight">
              What you’ll build & learn
            </h2>
            <div className="mt-8 space-y-6 text-neutral-700 leading-relaxed">
              {it.long.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white border border-neutral-200 rounded-2xl p-8">
              <div className="text-xs font-mono uppercase tracking-[0.22em] text-neutral-500">Skills you gain</div>
              <ul className="mt-6 space-y-3">
                {it.skills.map((s) => (
                  <li key={s} className="flex items-center gap-3 text-sm text-neutral-800">
                    <CheckCircle2 className="w-4 h-4 text-neutral-950" strokeWidth={1.75} />
                    {s}
                  </li>
                ))}
              </ul>
              <Link to={`/apply?domain=${encodeURIComponent(it.title)}`} className="btn-pill btn-pill--primary mt-10 w-full" data-testid="detail-enroll-cta">
                Enroll in this Program
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-neutral-200 rounded-2xl p-6 flex items-start gap-4">
      <div className="w-10 h-10 rounded-full border border-neutral-200 grid place-items-center">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</div>
        <div className="mt-1 text-neutral-900">{value}</div>
      </div>
    </div>
  );
}
